/**
 * **콜팝 운영사·관리점 화면의 문구 한 곳** — 의존성이 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 서버(`/callpop/**`)는 어휘(`targetTp`·`action`·`reasons`·`autoJoin`·`status`)만 내리고 한글 문장은
 * 프론트가 만든다 — `partnerLink.js`·`deliveryMenuNotes.js` 와 같은 결이다. 화면 넷(운영사·관리점 관리 ·
 * 매장 입점 현황 · 신규 매장 등록 · 변경 이력)과 계정 발급 화면·외부 채널 탭이 **같은 낱말**을 써야 하므로
 * 문장을 컴포넌트에 다시 타이핑하지 않는다.
 *
 * 판정·변환은 [callpopOrg.js](./callpopOrg.js) 가 갖는다 — 이 파일에는 값이 없다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/B7b-운영사관리점-appserver-report.md` §3.
 */

export const CALLPOP_TEXT = Object.freeze({
  // ── 공통 ────────────────────────────────────────────────────────────────
  operatorLabel: '운영사',
  agencyLabel: '관리점',
  storeLabel: '매장',
  hqTag: '총괄사',
  activeTag: '활성',
  inactiveTag: '비활성',
  /** 못 보는 대상은 **404** 로 온다 — 존재를 알려 주지 않는 서버 규칙(§3-1)이라 화면도 한 문장으로 답한다 */
  notFound: '찾을 수 없습니다. 이미 지워졌거나 조회 범위 밖입니다.',
  loadFailed: '목록을 불러오지 못했습니다.',
  retryAction: '다시 시도',
  searchAction: '조회',
  addAction: '등록',
  editAction: '수정',
  saveAction: '저장',
  cancelAction: '취소',
  closeAction: '닫기',
  confirmAction: '확인',
  copyAction: '복사',
  copied: '복사했습니다.',
  copyFailed: '복사하지 못했습니다. 값을 직접 선택해 복사하세요.',
  requiredFieldMissing: '필수 값을 채워 주세요.',
  /**
   * **계정 ID 채번이 겹쳤다**(409, 서버가 「이미 있는 계정입니다.」로 답한다).
   *
   * 사람이 입력한 ID 가 아니라 서버가 만든 ID 라, 서버 문구를 그대로 보이면 「내가 뭘 잘못 넣었나」로
   * 읽힌다. 이 실패는 **다시 누르면 풀리는 경합**이므로 그렇게 말한다(입력은 그대로 남는다).
   */
  accountIdConflict: '계정을 만드는 중에 ID 가 겹쳤습니다. 잠시 후 다시 시도해 주세요.',

  // ── 화면 #1 운영사·관리점 관리 ─────────────────────────────────────────
  orgTitle: '운영사·관리점 관리',
  /**
   * 두 목록은 **평소 활성만** 보인다(R-A, E-26 약속). 켜면 비활성 행이 흐리게 함께 선다 — 재활성은 그때만 누를 수 있다.
   * 서버의 코드 충돌 409 문장(「목록에서 「비활성 포함」을 켜고 재활성하십시오.」)이 **이 낱말**을 가리키므로 바꾸지 않는다.
   */
  includeInactiveLabel: '비활성 포함',
  operatorListTitle: '운영사',
  operatorAddAction: '운영사 등록',
  operatorEditAction: '운영사 수정',
  operatorOffAction: '비활성',
  operatorOnAction: '재활성',
  operatorWaiveAction: '권리 포기',
  operatorCodeLabel: '운영사 코드',
  operatorCodeHint: '영문 두 자입니다. 등록 뒤에는 바꿀 수 없습니다.',
  operatorCodeLocked: '운영사 코드는 등록 뒤 바꿀 수 없습니다.',
  operatorNmLabel: '운영사명',
  hqYnLabel: '총괄사',
  hqYnHint: '총괄사는 활성 운영사 중 하나뿐입니다.',
  mgrNmLabel: '담당자',
  mgrTelLabel: '담당자 연락처',
  mobileLabel: '담당자 휴대폰',
  memoLabel: '메모',
  agencyCountColumn: '관리점 수',
  storeCountColumn: '매장 수',
  accountCountColumn: '계정 수',
  issuedAtColumn: '발급일',
  stateColumn: '상태',
  operatorEmpty: '등록된 운영사가 없습니다.',
  operatorPickHint: '운영사를 고르면 그 아래 관리점이 보입니다.',
  operatorOffConfirm: '이 운영사를 비활성합니다. 비활성 운영사로는 매장을 이관할 수 없습니다.',
  operatorOnConfirm: '이 운영사를 다시 활성합니다.',
  operatorWaiveConfirm:
    '이 운영사의 매장을 모두 총괄사 직할로 옮기고 권리를 포기합니다. 되돌리려면 매장을 하나씩 다시 이관해야 합니다.',
  operatorWaiveTitle: '권리 포기',

  agencyTableTitle: '관리점',
  agencyAddAction: '관리점 등록',
  agencyEditAction: '관리점 수정',
  agencyImportAction: '엑셀 일괄 등록',
  agencyCodeLabel: '관리점 코드',
  agencyCodeAutoHint: '비우면 저장할 때 자동으로 채번합니다.',
  agencyNmLabel: '관리점명',
  agencyEmpty: '등록된 관리점이 없습니다.',
  agencyPickOperatorFirst: '왼쪽에서 운영사를 먼저 고르세요.',
  agencyOffAction: '비활성',
  agencyOnAction: '재활성',
  agencyOffConfirm: '이 관리점을 비활성합니다.',
  agencyOnConfirm: '이 관리점을 다시 활성합니다.',
  agencyDrillDownAction: '소속 매장',
  /**
   * 코드를 직접 넣으면 **서버가 그 질의어를 어떤 코드로 읽는지** 보여 준다(`agencies/search` 의 코드 해석).
   *
   * 0행을 「아직 쓰이지 않은 코드」라고 말하지 않는다 — 그 조회는 **이 계정이 볼 수 있는 범위** 안에서
   * 찾고 활성 관리점만 준다. 못 찾은 것과 없는 것은 다른 사실이고, 유일성의 판정은 저장할 때 서버가 한다.
   * 코드가 **비활성** 관리점의 것이면 저장의 409 가 그 상태와 재활성 길을 말한다(R-A) — 이 문장은 그 답을
   * 미리 부정하지 않도록 「활성」을 적는다.
   */
  agencyCodePreviewNone: '그 코드로 찾은 활성 관리점이 없습니다. 비활성 관리점과 겹치면 저장할 때 알려 드립니다.',
  agencyCodePreviewTaken: '이미 있는 관리점입니다',

  // ── 관리점 비활성 → 이관 목적지 ────────────────────────────────────────
  transferTitle: '소속 매장 이관',
  transferDestLabel: '옮길 곳',
  transferReasonLabel: '사유',
  transferAction: '이관하고 비활성',
  transferDestPlaceholder: '옮길 곳을 고르세요',
  transferDirect: '직할',
  transferHqDirect: '총괄사 직할',
  transferNoDestination: '옮길 곳이 없습니다. 운영사나 관리점을 먼저 만드세요.',

  // ── 엑셀 일괄 등록 ─────────────────────────────────────────────────────
  importTitle: '관리점 엑셀 일괄 등록',
  importPickFile: '파일 선택',
  importTemplateAction: '양식 내려받기',
  importPreviewAction: '미리 보기',
  importUploadAction: '업로드',
  importFileHint: '.xlsx 또는 .xls, 한 번에 1,000행까지. 헤더 1행은 양식과 같아야 합니다.',
  importNoFile: '파일을 먼저 고르세요.',
  importRowColumn: '행',
  importResultColumn: '결과',
  importMessageColumn: '메시지',
  importResultCreate: '등록',
  importResultError: '오류',
  importHasError: '오류 행이 있으면 아무것도 저장되지 않습니다. 파일을 고친 뒤 다시 미리 보기를 하세요.',
  importDone: '관리점을 등록했습니다.',
  importTemplateFileName: '관리점_일괄등록_양식.xlsx',

  // ── 화면 #2 매장 입점 현황 ─────────────────────────────────────────────
  storeListTitle: '매장 입점 현황',
  storeListTitleForAgency: '관리 매장 목록',
  storeNmColumn: '매장',
  busiNumColumn: '사업자번호',
  storeAddrColumn: '주소',
  ownerColumn: '운영사 / 관리점',
  mappedAtColumn: '매핑일',

  tvColumn: 'TV 입점',
  /** 원천은 `tbl_store.start_dt`(가입일 일시) — 서버 보고서 §8 1 */
  regDtmColumn: '매장 등록일',
  joinedAtColumn: '콜팝 입점일',
  pgContractColumn: 'PG 계약 완료일',
  eligibilityColumn: '참여 조건',
  searchPlaceholder: '매장명·매장 ID·사업자번호',
  /** 열 머리글과 필터 라벨이 **같은 낱말**이다 — 두 상수로 두면 한쪽만 고쳐질 때 화면이 두 말을 한다 */
  participationLabel: '참여 상태',
  eligibilityFilterLabel: '참여 조건',
  eligibilityEligible: '충족',
  eligibilityIneligible: '미달',
  /**
   * 콜팝 주문 실적(R-V A) — 기간 안 콜팝 앱·콜게이트 전화주문 중 유효 주문(취소 아님, 선불은 결제 완료)의 건수와
   * 결제 금액(할인 뒤 고객 부담액) 합. 판정은 서버가 한다(`CallpopOrderPerformance`)
   */
  ordersPeriodLabel: '실적 기간',
  ordersCountLabel: '콜팝 주문',
  ordersAmountLabel: '결제',
  ordersCountUnit: '건',
  ordersAmountUnit: '원',
  /**
   * 앱 노출(11-03) — 콜팝 소유·운영 중(OPEN) 플레이스 중 그 매장이 편성된 곳의 수(서버 `appPlaceCount`). 참여(「참여 중」)와
   * 다른 축이다: 참여 조건 미달 자동 제외(03:50)는 편성만 빼고 참여 등록은 그대로 둔다
   */
  appPlaceLabel: '앱 노출',
  appPlaceUnit: '곳',
  appPlaceNone: '없음',
  appPlaceTitle:
    '콜팝 앱에는 운영 중인 콜팝 플레이스에 편성된 매장만 보입니다. 참여 중이어도 편성이 없으면 앱에 보이지 않습니다.',
  mappingNone: '매핑 없음',
  confirmTitle: '확인',
  storeInactive: '비활성 매장',
  storeInactiveTip: '지워진 매장입니다. 매핑 행은 남아 있어 소속 매장 수에 계속 잡힙니다.',
  mappingAction: '매핑',
  transferMappingAction: '이관',
  historyAction: '이력',
  pgContractAction: 'PG 계약일',
  joinBulkAction: '일괄 참여 등록',
  storeRegisterAction: '신규 매장 등록',
  autoJoinRunAction: '자동 참여 실행',
  autoJoinRunConfirm: '조건을 채운 매핑 매장을 지금 한 번 참여시킵니다.',
  storeEmpty: '해당하는 매장이 없습니다.',
  /** `size` 상한 200 — 넘기면 서버가 400 이다(§3-4) */
  sizeOverMax: '한 번에 200건까지 볼 수 있습니다.',

  // ── 매핑 다이얼로그 ────────────────────────────────────────────────────
  mappingTitle: '관리점 매핑',
  mappingDestLabel: '매핑할 곳',
  mappingReasonLabel: '사유',
  mappingMemoLabel: '메모',
  mappingCurrentLabel: '현재',
  mappingDestOperator: '운영사 직할',
  mappingDestAgency: '관리점',
  mappingSaveAction: '매핑',
  mappingDenied: '이 매장은 이관할 수 없습니다.',

  // ── 참여 등록·일괄 ─────────────────────────────────────────────────────
  joinIneligibleTip: '참여 조건을 채우지 못했습니다',
  joinBulkTitle: '콜팝 참여 일괄 등록',
  joinBulkConfirm: '고른 매장을 콜팝 참여 매장으로 등록합니다. 조건을 못 채운 매장은 건너뜁니다.',
  joinBulkEmpty: '매장을 먼저 고르세요.',
  joinBulkJoined: '참여 등록',
  joinBulkAlreadyActive: '이미 참여 중',
  joinBulkSkipped: '조건 미달',
  joinBulkNotFound: '찾을 수 없음',
  joinBulkOutOfScope: '조회 범위 밖',
  joinBulkRequested: '요청',

  // ── PG 계약 완료일 ─────────────────────────────────────────────────────
  pgContractTitle: 'PG 계약 완료일',
  pgContractHint: '오더엔 내부만 입력합니다. 비우면 지워집니다.',
  pgContractSaved: 'PG 계약 완료일을 저장했습니다.',

  // ── 화면 #3 신규 매장 등록 ─────────────────────────────────────────────
  registerTitle: '신규 매장 등록',
  registerStep1: '사업자번호 확인',
  registerStep2: '매장 정보',
  registerStep3: '등록 결과',
  registerAgencyLabel: '등록할 관리점',
  registerAgencyFixed: '자기 관리점으로 등록됩니다.',
  busiNumLabel: '사업자번호',
  busiNumCheckAction: '확인',
  busiNumPlaceholder: '숫자 10자리',
  /**
   * **새 매장은 사업장을 새로 만들지 않는다**(후속 2 §1-2, 2026-09-21 결정) — 콜팝 등록 경로의 새 매장은
   * 「콜팝」 사업장 하나에 직접 달리고 사업장 계정(C)도 만들지 않는다. 종전 문구(「매장과 사업장을 새로
   * 만듭니다」)는 매장마다 사업장을 하나씩 만들던 첫 구현의 말이라 지금은 거짓이다.
   */
  bizNewNote: '새 사업자입니다. 매장을 새로 만듭니다.',
  bizSiblingNote: '이미 등록된 사업자입니다. 아래 매장 중 하나에 샵인샵으로 추가할 수 있습니다.',
  /**
   * 샵인샵 추가가 **C 레벨을 한 단계 더 만들 수 있다**는 사실(§1-2 「그 밖」 갈래) — 대표 매장이 그룹
   * 사업장에 있지 않으면 그 아래에 이 상점주의 그룹 사업장이 새로 서고 대표 계정(C)이 한 번 발급된다.
   * 「있을 수 있습니다」인 이유: 이미 그룹 사업장이면 재사용이라 계정이 생기지 않는다 —
   * **무엇이 일어났는지는 등록 결과 화면의 계정 줄이 사실로 답한다**([registerResultAccounts]).
   */
  bizSiblingAccountNote: '샵인샵으로 추가하면 이 상점주의 매장을 묶는 사업장과 대표 계정(C)이 만들어질 수 있습니다.',
  bizSiblingAction: '샵인샵으로 추가',
  bizSiblingPicked: '샵인샵으로 추가할 매장',
  bizInvalidPrefix: '사업자번호를 확인해 주세요',
  ceoNmLabel: '대표자',
  custNmLabel: '상호',
  storeNmLabel: '매장명',
  telLabel: '매장 전화',
  addrLabel: '매장 주소',
  addrPickAction: '주소 조회',
  addrDtlLabel: '상세 주소',
  categoryLabel: '업종',
  busiEmailLabel: '이메일',
  registerAction: '등록',
  registerAgainAction: '다른 매장 등록',
  registerDone: '매장을 등록했습니다.',
  resultStoreIdLabel: '매장 ID',
  resultCustIdLabel: '사업장 ID',
  resultStoreAccountLabel: '매장 관리자 계정',
  resultCustAccountLabel: '사업장 관리자 계정',
  resultPasswordLabel: '초기 비밀번호',
  /** 비밀번호는 이 응답에 한 번뿐이다(서버 §3-8) — 화면이 그 사실을 먼저 말한다 */
  resultPasswordOnce: '이 화면을 닫으면 다시 볼 수 없습니다.',
  resultPasswordChange: '첫 로그인 뒤 담당자별로 비밀번호를 바꿔 주세요.',
  resultMappingLabel: '매핑',
  resultParticipationLabel: '참여',
  registerGoList: '매장 입점 현황으로',

  // ── 화면 #4 변경 이력 ──────────────────────────────────────────────────
  historyTitle: '변경 이력',
  historyTargetTpLabel: '대상 종류',
  historyTargetLabel: '대상',
  historyPeriodLabel: '기간',
  historyAtColumn: '시각',
  historyTargetColumn: '대상',
  historyActionColumn: '동작',
  historyDetailColumn: '상세',
  historyActorColumn: '행위자',
  historyEmpty: '변경 이력이 없습니다.',
  /** 운영사·관리점 계정은 대상 지목 없이 부르면 서버가 400 이다(§3-5) — 화면이 먼저 말한다 */
  historyTargetRequired: '대상을 먼저 고르세요. 이 계정은 전체 목록을 볼 수 없습니다.',
  historyTargetStorePlaceholder: '매장 코드(숫자)',
  /**
   * 숫자로 안 읽히는 대상 입력 — 매장 축은 자유 입력이라 매장 ID(`S260001287`)나 매장명이 들어온다.
   * 그대로 보내면 400 이고, 그 400 은 「대상을 지목하라」와 구분되지 않는다. **보내지 않고 여기서 말한다.**
   */
  historyTargetNotNumber: '숫자로 된 매장 코드를 넣으세요. 매장 ID(S로 시작)나 매장명으로는 찾을 수 없습니다.',
  historyBefore: '이전',
  historyAfter: '이후',
  /**
   * 권리 포기·관리점 비활성의 `detail` 두 칸 — 접미(`…OperatorCd`)로는 **「옮긴」이 안 읽힌다**.
   *
   * 축 이름은 `callpopOrg.js` 의 접미 규칙이 붙이므로(「운영사」), 여기에는 그 규칙이 모르는 뜻만 적는다.
   */
  movedToOperatorLabel: '옮긴 운영사',
  movedStoreCountLabel: '옮긴 매장',
  /** 수 뒤에 붙는 낱말 — 「옮긴 매장: 2곳」 */
  storeCountUnit: '곳',

  // ── 어휘 → 한글 (판정은 callpopOrg.js) ─────────────────────────────────
  targetOperator: '운영사',
  targetAgency: '관리점',
  targetStoreMap: '매장 매핑',
  actionCreated: '등록',
  actionUpdated: '수정',
  actionDeactivated: '비활성',
  actionReactivated: '재활성',
  actionImported: '엑셀 등록',
  actionMapped: '매핑',
  actionTransferred: '이관',
  actionWaived: '권리 포기',
  actionUnmapped: '매핑 해제',

  reasonNoMid: 'PG(MID) 설정 없음',
  reasonNoMenu: '판매 중 메뉴 없음',
  reasonTransitionFailed: '참여 전이 실패',

  autoJoinJoined: '참여 등록까지 함께 됐습니다.',
  autoJoinAlreadyActive: '이미 참여 중인 매장입니다.',
  autoJoinEnded: '해지된 매장이라 자동으로 되살리지 않았습니다. 참여 등록은 따로 합니다.',
  autoJoinIneligible: '참여 조건을 채우지 못해 등록은 대기합니다.',
  /** 실패해도 **매핑은 남는다**(§3-5) — 그 사실을 문장이 말하지 않으면 사람이 매핑을 다시 누른다 */
  autoJoinFailed: '매핑은 저장됐지만 참여 등록은 실패했습니다. 참여는 따로 등록해 주세요.',

  bizStatusNew: '새 사업자',
  bizStatusExistsVisible: '이미 등록된 사업자',
  bizStatusExistsHidden: '이미 등록된 사업자',
  bizStatusInvalid: '형식 오류',
  bizReasonEmpty: '사업자번호를 입력하세요.',
  bizReasonLength: '숫자 10자리여야 합니다.',
  bizReasonChecksum: '사업자번호 검증을 통과하지 못했습니다.',
  bizReasonDummy: '쓸 수 없는 사업자번호입니다.',
})
